import {AsyncPipe, SlicePipe} from '@angular/common';
import {AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {SbbAutocomplete, SbbAutocompleteTrigger} from '@sbb-esta/lyne-angular/autocomplete';
import {SbbButton} from '@sbb-esta/lyne-angular/button';
import {SbbError} from '@sbb-esta/lyne-angular/form-field';
import {SbbFormField} from '@sbb-esta/lyne-angular/form-field';
import {SbbLoadingIndicator} from '@sbb-esta/lyne-angular/loading-indicator';
import {SbbOption} from '@sbb-esta/lyne-angular/option';
import {SbbOptionHint} from '@sbb-esta/lyne-angular/option';
import {PartnerApiModel} from 'cargo-hub-ui-customer-api';
import {EMPTY, firstValueFrom, Observable, Subject} from 'rxjs';
import {catchError, filter, finalize, map, shareReplay, switchMap} from 'rxjs/operators';
import {FilterHelper} from '../../cd-common/helper/filter-helper';
import {PartnerHelper, ZkkPartner} from '../../model/partner-helper';
import {PartnerService} from '../../services/partner/partner.service';
import {zkkSelectedAction} from '../auth-store/auth.actions';
import {AuthState} from '../auth-store/auth.reducer';
import {selectUser} from '../auth-store/auth.selectors';

@Component({
  selector: 'app-zkk-selection',
  templateUrl: './zkk-selection.component.html',
  styleUrls: ['./zkk-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, FormsModule, SbbFormField, SbbError, SbbAutocomplete, SbbAutocompleteTrigger, SbbOption, SbbLoadingIndicator, SbbOptionHint, SbbButton, AsyncPipe, SlicePipe]
})
export class ZkkSelectionComponent implements OnInit, AfterViewInit {
  readonly MAX_PARTNERS = 10;
  readonly VIRTUAL_PARTNER: ZkkPartner = {zkk: '00009999', name: 'Storage unbekannte Partnernummer', datentyp: 'ZKK'};

  @ViewChild('partnerSelection') input: ElementRef;

  searchControl = new FormControl<ZkkPartner | null>(null, Validators.required);
  nativeInputChanged$ = new Subject<string>();
  options$: Observable<ZkkPartner[]>;

  arePartnersLoading: boolean;
  isErrorLoadingPartners: boolean;

  constructor(
    private partnerService: PartnerService,
    private cd: ChangeDetectorRef,
    private store: Store<{auth: AuthState}>
  ) {}

  ngOnInit() {
    this.options$ = this.nativeInputChanged$.pipe(
      filter(v => v.length >= 3),
      switchMap(partner => this.getPartnersByIdOrName(partner)),
      shareReplay(1)
    );
  }

  ngAfterViewInit() {
    // wrap call in promise as workaround to prevent ExpressionChangedAfterItHasBeenCheckedError
    Promise.resolve().then(() => this.input.nativeElement.focus());
  }

  async applyPartner() {
    const selectedPartner = this.searchControl.value;
    if (PartnerHelper.instanceOfPartner(selectedPartner)) {
      const user = await firstValueFrom(this.store.pipe(select(selectUser)));
      const isSwitchByServiceUser = !!user && !!user.activePartner && user.activePartner.partnerId !== selectedPartner.zkk;
      const selectedPartnerStammdaten = await firstValueFrom(this.partnerService.getPartnerById(selectedPartner.zkk));
      this.store.dispatch(zkkSelectedAction({newPartner: selectedPartnerStammdaten, isSwitch: isSwitchByServiceUser}));
    }
  }

  display(p: PartnerApiModel | null): string {
    return PartnerHelper.instanceOfPartner(p) ? PartnerHelper.displayPartnerAsString(p) : '';
  }

  /**
   * loads partners for given partialPartnerId
   * adds a timeout to show loading animation after 300ms
   *
   * @param partialPartnerIdOrName search query
   */
  getPartnersByIdOrName(partialPartnerIdOrName: string): Observable<Array<ZkkPartner>> {
    this.isErrorLoadingPartners = false;
    const loadingTimer = setTimeout(() => {
      this.arePartnersLoading = true;
      this.cd.markForCheck();
    }, 300);

    return this.partnerService.getPartnersBySearch(partialPartnerIdOrName).pipe(
      map(partners => {
        if (FilterHelper.matchesPartner(partialPartnerIdOrName, this.VIRTUAL_PARTNER)) {
          partners.push(this.VIRTUAL_PARTNER);
        }
        return partners.sort((partner1: PartnerApiModel, partner2: PartnerApiModel) => `${partner1.name}`.localeCompare(`${partner2.name}`));
      }),
      catchError(e => {
        console.log('failed to load partners', e);
        this.isErrorLoadingPartners = true;
        return EMPTY;
      }),
      finalize(() => {
        clearTimeout(loadingTimer);
        this.arePartnersLoading = false;
      })
    );
  }
}
